import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { FiTrendingUp } from 'react-icons/fi'
import { fetchWrapper } from '../../utils/apiHandlers'
import { useShopLocation } from '../../utils/shopLocation'
import theme from '../../theme'

interface Props {
  onPick: (text: string) => void   // the exact text to ask
  // Shown while there isn't enough real activity yet - clearly labelled as suggestions, not trends.
  fallback: string[]
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

// A trending topic is a bare label ("brunch", "cake"); ask it as a request. Suggestions are already sentences and are sent as written.
const asRequest = (topic: string) => `I'm looking for ${topic}`

const TrendingPanel: React.FC<Props> = ({ onPick, fallback }) => {
  const { location, ready } = useShopLocation()
  const [data, setdata] = useState<{ scope: string; city: string; topics: { topic: string; count: number }[] } | null>(null)

  useEffect(() => {
    if (!ready) return
    const q = location ? `?lat=${location.lat}&lon=${location.lon}&city=${encodeURIComponent(location.city)}&state=${encodeURIComponent(location.state)}` : ''
    fetchWrapper.get(`/api/directory/trending${q}`).then((r: any) => setdata(r)).catch(() => setdata(null))
  }, [ready, location])

  const topics = data ? data.topics : []
  const enough = topics.length >= 3
  const max = topics.reduce((m, t) => Math.max(m, t.count), 1)

  return (
    <Panel>
      <Title>
        <FiTrendingUp />
        {enough ? (data!.scope === 'city' ? `Trending in ${data!.city}` : 'Trending now') : 'Try asking'}
      </Title>
      {enough ? (
        <List>
          {topics.map((t) => (
            <Item key={t.topic} onClick={() => onPick(asRequest(t.topic))}>
              <ItemTop>
                <span>{cap(t.topic)}</span>
                <Count>{t.count}</Count>
              </ItemTop>
              <Bar><BarFill style={{ width: `${Math.max(8, (t.count / max) * 100)}%` }} /></Bar>
            </Item>
          ))}
        </List>
      ) : (
        <List>
          {fallback.map((f) => (
            <Suggestion key={f} onClick={() => onPick(f)}>{f}</Suggestion>
          ))}
        </List>
      )}
      <Note>{enough ? 'What people nearby are asking about. Tap one to ask.' : 'Trends appear here as people use the assistant.'}</Note>
    </Panel>
  )
}

const Panel = styled.aside`
  background: ${theme.colors.surface};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.raised};
  padding: 18px;
  width: 100%;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  font-size: 16px;
  color: ${theme.colors.primaryDark};
  margin-bottom: 12px;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Item = styled.button`
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  color: ${theme.colors.text};
  &:hover span:first-child {
    color: ${theme.colors.primary};
  }
`

const ItemTop = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: 600;
`

const Count = styled.span`
  color: ${theme.colors.textMuted};
  font-weight: 500;
`

const Bar = styled.div`
  height: 5px;
  border-radius: 3px;
  background: ${theme.colors.primarySoft};
  margin-top: 4px;
`

const BarFill = styled.div`
  height: 100%;
  border-radius: 3px;
  background: ${theme.colors.primary};
`

const Suggestion = styled.button`
  text-align: left;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  border-radius: ${theme.radius.md};
  padding: 9px 12px;
  font-size: 13px;
  color: ${theme.colors.text};
  cursor: pointer;
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`

const Note = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: ${theme.colors.textMuted};
`

export default TrendingPanel
